import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DialogClose } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { AmendPayload } from '@/shared/types/tryIt';

import { ContextTab } from './ContextTab';
import { ConversationTab } from './ConversationTab';
import { DetailsTab } from './DetailsTab';
import { InsightsTab } from './InsightsTab';
import { ResultsTab } from './ResultsTab';
import type { EvaluationWithSimulation } from '../../../types';

interface RightPanelProps {
  evaluation: EvaluationWithSimulation;
  hasTurnSelected: boolean;
  detailsContent: { session_id?: string; brain_parent_id?: string };
  simulationContext: NonNullable<AmendPayload['simulation']>;
  selectedRequestId: string | undefined;
}

export const RightPanel = ({
  evaluation,
  hasTurnSelected,
  detailsContent,
  simulationContext,
  selectedRequestId,
}: RightPanelProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-w-0 flex-1 basis-0 flex-col overflow-hidden">
      <Tabs defaultValue="results" className="flex min-h-0 flex-1 flex-col">
        <div className="relative flex h-14 shrink-0 items-end px-4">
          <TabsList variant="line">
            <TabsTrigger variant="line" value="results" className="font-semibold">
              {t('simulations.results')}
            </TabsTrigger>
            <TabsTrigger variant="line" value="details" className="font-semibold">
              {t('simulations.details')}
            </TabsTrigger>
            <TabsTrigger variant="line" value="context" className="font-semibold">
              {t('simulations.context')}
            </TabsTrigger>
            <TabsTrigger variant="line" value="conversation" className="font-semibold">
              {t('simulations.conversation')}
            </TabsTrigger>
            <TabsTrigger variant="line" value="insights" className="font-semibold">
              {t('insights.title')}
            </TabsTrigger>
          </TabsList>
          <DialogClose
            className="absolute top-3 right-3 inline-flex size-8 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-bg-muted-50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            aria-label={t('common.close')}
          >
            <XIcon className="size-4" />
          </DialogClose>
        </div>

        <ResultsTab evaluation={evaluation} />
        <DetailsTab
          hasTurnSelected={hasTurnSelected}
          detailsContent={detailsContent}
          simulationContext={simulationContext}
        />
        <ContextTab hasTurnSelected={hasTurnSelected} />
        <ConversationTab
          evaluation={evaluation}
          hasTurnSelected={hasTurnSelected}
          selectedRequestId={selectedRequestId}
        />
        <InsightsTab evaluation={evaluation} />
      </Tabs>
    </div>
  );
};
